import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import {
  ARCHETYPE_LABEL,
  BASIS_LABEL,
  ROLE_LABEL,
  fmtRate,
  useContractContext,
  usePayees,
  useSaveAssignment,
  type Archetype,
  type Assignment,
  type Basis,
  type Role,
} from "../api";

/** Create/edit one payee's rate window on a contract (admin-only). */
export function AssignmentDialog({
  open,
  onOpenChange,
  contractId,
  existing,
  siblings,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId: string;
  existing: Assignment | null;
  siblings: Assignment[];
}) {
  const payees = usePayees(open);
  const context = useContractContext(contractId, open && !existing);
  const save = useSaveAssignment(contractId);

  const [payeeId, setPayeeId] = useState("");
  const [role, setRole] = useState<Role>("ae");
  const [archetype, setArchetype] = useState<Archetype | "">("");
  const [mode, setMode] = useState<"rate" | "fixed">("rate");
  const [rate, setRate] = useState(""); // percent, e.g. "15"
  const [fixed, setFixed] = useState("");
  const [basis, setBasis] = useState<Basis>("billed");
  const [baseline, setBaseline] = useState("");
  const [monthFrom, setMonthFrom] = useState("1");
  const [monthTo, setMonthTo] = useState("12");
  const [windowStart, setWindowStart] = useState("");
  const [signDate, setSignDate] = useState("");
  const [firstBilling, setFirstBilling] = useState("");
  const [p1Override, setP1Override] = useState<boolean | null>(null);
  const [scope, setScope] = useState("");
  const [notes, setNotes] = useState("");

  // Populate from the record being edited, or reset for a fresh one.
  useEffect(() => {
    if (!open) return;
    if (existing) {
      setPayeeId(existing.payee_id);
      setRole(existing.role);
      setArchetype(existing.contract_archetype ?? "");
      setMode(existing.fixed_monthly_amount != null ? "fixed" : "rate");
      setRate(existing.rate_pct != null ? String(existing.rate_pct * 100) : "");
      setFixed(
        existing.fixed_monthly_amount != null ? String(existing.fixed_monthly_amount) : "",
      );
      setBasis(existing.basis);
      setBaseline(
        existing.replacement_baseline_mrr != null
          ? String(existing.replacement_baseline_mrr)
          : "",
      );
      setMonthFrom(String(existing.month_from));
      setMonthTo(String(existing.month_to));
      setWindowStart(existing.window_start ?? "");
      setSignDate(existing.sign_date ?? "");
      setFirstBilling(existing.first_billing_date ?? "");
      setP1Override(null);
      setScope(existing.item_scope?.join(", ") ?? "");
      setNotes(existing.notes ?? "");
    } else {
      setPayeeId("");
      setRole("ae");
      setArchetype("");
      setMode("rate");
      setRate("");
      setFixed("");
      setBasis("billed");
      setBaseline("");
      setMonthFrom("1");
      setMonthTo("12");
      setWindowStart("");
      setSignDate("");
      setFirstBilling("");
      setP1Override(null);
      setScope("");
      setNotes("");
    }
  }, [open, existing]);

  // The contract itself carries its type, sign date, and first billing date —
  // those are FACTS off the contract, not user choices. When the warehouse
  // context loads they're locked in (read-only below); manual inputs remain
  // only as a fallback when no context exists (e.g. warehouse down).
  const ctx = context.data ?? null;
  useEffect(() => {
    if (!open || existing || !ctx) return;
    if (ctx.contract_signed_at) setSignDate(ctx.contract_signed_at);
    if (ctx.billing_effective_from) {
      setFirstBilling(ctx.billing_effective_from);
      setWindowStart(`${ctx.billing_effective_from.slice(0, 7)}-01`);
    }
    const type = (ctx.contract_type_name ?? "").toLowerCase();
    if (type.includes("replacement")) {
      setArchetype("replacement");
      setBasis("replacement_delta");
    } else if (type.startsWith("plg")) {
      setArchetype("plg");
      setRole("product");
    } else if (type.includes("upsell")) {
      setArchetype("upsell");
    } else if (type.includes("new contract")) {
      setArchetype("new");
    } else if (type.includes("purchase order")) {
      setArchetype("po");
    } else if (type.includes("reseller")) {
      setArchetype("reseller");
    } else if (type.includes("license")) {
      setArchetype("license");
    }
  }, [open, existing, ctx]);

  // The contract's CLOSER is the default first payee: preselect the payee
  // whose HubSpot owner id or name matches the closer.
  const payeeRows = payees.data ?? [];
  useEffect(() => {
    if (!open || existing || !ctx || payeeId || payeeRows.length === 0) return;
    const byHubspot =
      ctx.closer_hubspot_owner_id != null
        ? payeeRows.find(
            (p) => p.hubspot_owner_id === String(ctx.closer_hubspot_owner_id),
          )
        : undefined;
    const byName = ctx.closer_name
      ? payeeRows.find(
          (p) => p.display_name.toLowerCase() === ctx.closer_name!.toLowerCase(),
        )
      : undefined;
    const match = byHubspot ?? byName;
    if (match) setPayeeId(match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing, ctx, payeeRows.length]);

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!existing && !payeeId) e.payee = "Pick a payee";
    if (mode === "rate") {
      const r = Number(rate);
      if (rate === "" || Number.isNaN(r)) e.rate = "Rate is required";
      else if (r < 0) e.rate = "Rate can't be negative";
      else if (r > 100) e.rate = "Rate can't exceed 100%";
    } else {
      const f = Number(fixed);
      if (fixed === "" || Number.isNaN(f)) e.fixed = "Amount is required";
      else if (f < 0) e.fixed = "Amount can't be negative";
    }
    const from = Number(monthFrom);
    const to = Number(monthTo);
    if (!Number.isInteger(from) || from < 1 || from > 24) e.monthFrom = "1–24";
    if (!Number.isInteger(to) || to < 1 || to > 24) e.monthTo = "1–24";
    if (!e.monthFrom && !e.monthTo && from > to) e.monthTo = "Must be ≥ start month";
    const overlap = siblings.find(
      (s) =>
        s.id !== existing?.id &&
        s.payee_id === (existing?.payee_id ?? payeeId) &&
        s.status !== "ended" &&
        from <= s.month_to &&
        s.month_from <= to,
    );
    if (overlap && !e.monthFrom && !e.monthTo) {
      e.monthTo = `Overlaps M${overlap.month_from}–M${overlap.month_to} for this payee`;
    }
    if (basis === "replacement_delta" && baseline === "") {
      e.baseline = "Baseline MRR required for replacement delta";
    }
    return e;
  }, [existing, payeeId, mode, rate, fixed, monthFrom, monthTo, siblings, basis, baseline]);

  const p1Days = useMemo(() => {
    if (!signDate || !firstBilling) return null;
    return Math.abs(
      Math.round(
        (new Date(firstBilling).getTime() - new Date(signDate).getTime()) / 86_400_000,
      ),
    );
  }, [signDate, firstBilling]);
  const p1Auto = p1Days != null && p1Days <= 7;
  const p1Effective = p1Override ?? p1Auto;

  const submit = () => {
    const body = {
      contract_id: contractId,
      account_id: context.data?.account_id ?? undefined,
      quickbooks_id:
        context.data?.quickbooks_id != null ? String(context.data.quickbooks_id) : undefined,
      company_name: context.data?.company_name ?? undefined,
      contract_type_name: context.data?.contract_type_name ?? undefined,
      contract_archetype: (archetype || null) as Archetype | null,
      payee_id: payeeId,
      role,
      rate_pct: mode === "rate" ? Number(rate) / 100 : null,
      fixed_monthly_amount: mode === "fixed" ? Number(fixed) : null,
      basis,
      replacement_baseline_mrr: baseline !== "" ? Number(baseline) : null,
      item_scope: scope.trim()
        ? scope.split(",").map((s) => s.trim()).filter(Boolean)
        : null,
      month_from: Number(monthFrom),
      month_to: Number(monthTo),
      window_start: windowStart || null,
      sign_date: signDate || null,
      first_billing_date: firstBilling || null,
      includes_p1_override: p1Override,
      notes: notes || null,
    };
    save.mutate(
      { id: existing?.id, body },
      {
        onSuccess: (a) => {
          toast.success(
            existing
              ? "Assignment updated"
              : a.status === "pending_approval"
                ? "Assignment saved — off-plan rate needs approval"
                : "Assignment saved",
          );
          onOpenChange(false);
        },
        onError: (e) =>
          toast.error("Couldn't save", { description: (e as Error).message }),
      },
    );
  };

  const activePayees = (payees.data ?? []).filter((p) => p.is_active || p.id === payeeId);
  const planRate = existing?.plan_rate_pct;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit assignment" : "Add payee to contract"}</DialogTitle>
          <DialogDescription>
            {context.data?.company_name
              ? `${context.data.company_name} · ${context.data.contract_type_name ?? ""}`
              : contractId}
            {context.data?.closer_name ? ` · closer: ${context.data.closer_name}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {!existing && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ca-payee">Payee</Label>
              <Select value={payeeId} onValueChange={setPayeeId}>
                <SelectTrigger id="ca-payee" aria-invalid={!!errors.payee}>
                  <SelectValue placeholder="Pick a payee" />
                </SelectTrigger>
                <SelectContent>
                  {activePayees.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.payee && <p className="text-xs text-danger">{errors.payee}</p>}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ca-role">Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as Role)}>
              <SelectTrigger id="ca-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {ctx ? (
            // The contract carries its own type — a fact, not a choice.
            <div className="flex flex-col gap-1.5">
              <Label>Contract type</Label>
              <p className="flex h-10 items-center rounded-md border border-input bg-muted/40 px-3 text-sm">
                {ctx.contract_type_name ?? "—"}
                {archetype && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    (plan key: {ARCHETYPE_LABEL[archetype]})
                  </span>
                )}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ca-archetype">Contract type (plan key)</Label>
              <Select
                value={archetype || "none"}
                onValueChange={(v) => setArchetype(v === "none" ? "" : (v as Archetype))}
              >
                <SelectTrigger id="ca-archetype">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {Object.entries(ARCHETYPE_LABEL).map(([k, label]) => (
                    <SelectItem key={k} value={k}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label>Pay as</Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={mode === "rate" ? "default" : "outline"}
                onClick={() => setMode("rate")}
              >
                % of billing
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mode === "fixed" ? "default" : "outline"}
                onClick={() => setMode("fixed")}
              >
                Fixed $/month
              </Button>
            </div>
          </div>

          {mode === "rate" ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ca-rate">Rate (%)</Label>
              <Input
                id="ca-rate"
                inputMode="decimal"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                aria-invalid={!!errors.rate}
                placeholder="15"
              />
              {errors.rate && <p className="text-xs text-danger">{errors.rate}</p>}
              {planRate != null && (
                <p className="text-xs text-muted-foreground">
                  Plan default: {fmtRate(planRate)} — a different rate needs admin approval.
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ca-fixed">Fixed amount ($/month)</Label>
              <Input
                id="ca-fixed"
                inputMode="decimal"
                value={fixed}
                onChange={(e) => setFixed(e.target.value)}
                aria-invalid={!!errors.fixed}
                placeholder="400"
              />
              {errors.fixed && <p className="text-xs text-danger">{errors.fixed}</p>}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ca-basis">Basis</Label>
            <Select value={basis} onValueChange={(v) => setBasis(v as Basis)}>
              <SelectTrigger id="ca-basis">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(BASIS_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {basis === "replacement_delta" && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ca-baseline">Baseline monthly MRR ($)</Label>
              <Input
                id="ca-baseline"
                inputMode="decimal"
                value={baseline}
                onChange={(e) => setBaseline(e.target.value)}
                aria-invalid={!!errors.baseline}
                placeholder="Greater of prior-month billed vs replaced MRR"
              />
              {errors.baseline && <p className="text-xs text-danger">{errors.baseline}</p>}
              <p className="text-xs text-muted-foreground">
                Commission pays on the increase over this baseline (from the contract's
                replacement value in the Contracts section).
              </p>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ca-from">Window months</Label>
            <div className="flex items-center gap-2">
              <Input
                id="ca-from"
                className="w-20"
                inputMode="numeric"
                value={monthFrom}
                onChange={(e) => setMonthFrom(e.target.value)}
                aria-invalid={!!errors.monthFrom}
                aria-label="From month"
              />
              <span className="text-sm text-muted-foreground">to</span>
              <Input
                className="w-20"
                inputMode="numeric"
                value={monthTo}
                onChange={(e) => setMonthTo(e.target.value)}
                aria-invalid={!!errors.monthTo}
                aria-label="To month"
              />
            </div>
            {(errors.monthFrom || errors.monthTo) && (
              <p className="text-xs text-danger">{errors.monthFrom ?? errors.monthTo}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Stepped rates: save one row per range (e.g. 4% M1–3, then 8% M4–12).
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ca-window">First full billed month (M1)</Label>
            <Input
              id="ca-window"
              type="date"
              value={windowStart}
              onChange={(e) => setWindowStart(e.target.value)}
            />
          </div>

          {ctx ? (
            // Sign + first billing dates come off the contract itself.
            <div className="flex flex-col gap-1.5">
              <Label>Contract dates</Label>
              <p className="flex h-10 items-center rounded-md border border-input bg-muted/40 px-3 text-sm tabular-nums">
                signed {ctx.contract_signed_at ?? "—"} · first bill{" "}
                {ctx.billing_effective_from ?? "—"}
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ca-sign">Sign date</Label>
                <Input
                  id="ca-sign"
                  type="date"
                  value={signDate}
                  onChange={(e) => setSignDate(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ca-billing">First billing date</Label>
                <Input
                  id="ca-billing"
                  type="date"
                  value={firstBilling}
                  onChange={(e) => setFirstBilling(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="ca-p1">P1 stub month (the "13th month")</Label>
                <p className="text-xs text-muted-foreground">
                  {p1Days == null
                    ? "Earned when sign date and first billing are within 7 days."
                    : p1Auto
                      ? `Sign → first billing is ${p1Days} day${p1Days === 1 ? "" : "s"} — P1 earned.`
                      : `Sign → first billing is ${p1Days} days (over 7) — P1 not earned.`}
                  {p1Override != null && " (admin override)"}
                </p>
              </div>
              <Switch
                id="ca-p1"
                checked={p1Effective}
                onCheckedChange={(v) => setP1Override(v === p1Auto ? null : v)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="ca-scope">Item scope (optional)</Label>
            <Input
              id="ca-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder="e.g. Reputation Management, AI Voice — comma-separated; empty = all items"
            />
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="ca-notes">Notes</Label>
            <Input
              id="ca-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why this rate / any special terms"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={Object.keys(errors).length > 0 || save.isPending}
          >
            {save.isPending ? "Saving…" : existing ? "Save changes" : "Add payee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
